import {
  Anchor,
  Badge,
  Group,
  MultiSelect,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Text,
  TextInput,
} from '@mantine/core';
import {
  RISQ_PREP_TIMINGS,
  RISQ_STATUSES,
  VISIBLE_WORK_TYPES,
  type RisqPrepTiming,
  type RisqStatus,
  type VisibleWorkType,
} from '@opti-sea/risq';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import { useWorkplace } from '../../../core/access';
import { PageTitle, QueryState, StatusBadge, usePositionNames } from '../../../ui/components';
import { useVesselItems } from '../data';
import type { VesselItem } from '../model';
import { FlagBadges } from './flagBadges';

const UNASSIGNED = 'none';

export interface QuestionFilter {
  search: string;
  section: string | null;
  position: string | null;
  statuses: RisqStatus[];
  workTypes: VisibleWorkType[];
  timings: RisqPrepTiming[];
}

export const EMPTY_FILTER: QuestionFilter = {
  search: '',
  section: null,
  position: null,
  statuses: [],
  workTypes: [],
  timings: [],
};

/** Same filter for the vessel table and the mass reassignment (no timing flag counts as 3-m). */
export function filterQuestions<T extends Pick<VesselItem, 'question' | 'flags'>>(
  rows: readonly T[],
  filter: QuestionFilter,
  positionsOf: (row: T) => readonly string[],
  statusOf?: (row: T) => RisqStatus,
): T[] {
  const search = filter.search.trim().toLowerCase();
  return rows.filter(
    (r) =>
      (search === '' ||
        r.question.code.toLowerCase().startsWith(search) ||
        r.question.textEn.toLowerCase().includes(search)) &&
      (filter.section === null || r.question.sectionId === filter.section) &&
      (filter.position === null ||
        (filter.position === UNASSIGNED
          ? positionsOf(r).length === 0
          : positionsOf(r).includes(filter.position))) &&
      (filter.statuses.length === 0 ||
        statusOf === undefined ||
        filter.statuses.includes(statusOf(r))) &&
      (filter.workTypes.length === 0 ||
        r.flags.workTypes.some((w) => filter.workTypes.includes(w))) &&
      (filter.timings.length === 0 ||
        r.flags.effectiveTiming.some((p) => filter.timings.includes(p))),
  );
}

export function QuestionFilters({
  filter,
  onChange,
  sections,
  withStatus,
}: {
  filter: QuestionFilter;
  onChange: (next: QuestionFilter) => void;
  sections: { id: string; code: string }[];
  withStatus: boolean;
}) {
  const { t } = useTranslation(['dashboard', 'common']);
  const { access } = useWorkplace();
  const { name } = usePositionNames();
  const positionOptions = [
    ...access.positions.map((p) => ({ value: p.code, label: name(p.code) })),
    { value: UNASSIGNED, label: t('questions.unassigned') },
  ];
  return (
    <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} mb="md">
      <TextInput
        label={t('questions.search')}
        placeholder={t('questions.searchHint')}
        value={filter.search}
        onChange={(e) => onChange({ ...filter, search: e.currentTarget.value })}
      />
      <Select
        label={t('questions.section')}
        clearable
        data={sections.map((s) => ({
          value: s.id,
          label: t('questions.sectionCode', { code: s.code }),
        }))}
        value={filter.section}
        onChange={(section) => onChange({ ...filter, section })}
      />
      <Select
        label={t('questions.position')}
        clearable
        data={positionOptions}
        value={filter.position}
        onChange={(position) => onChange({ ...filter, position })}
      />
      {withStatus ? (
        <MultiSelect
          label={t('questions.status')}
          clearable
          data={RISQ_STATUSES.map((s) => ({ value: s, label: t(`common:status.${s}`) }))}
          value={filter.statuses}
          onChange={(values) =>
            onChange({ ...filter, statuses: RISQ_STATUSES.filter((s) => values.includes(s)) })
          }
        />
      ) : null}
      <MultiSelect
        label={t('questions.workTypes')}
        clearable
        data={VISIBLE_WORK_TYPES.map((w) => ({ value: w, label: t(`common:workTypes.${w}.name`) }))}
        value={filter.workTypes}
        onChange={(values) =>
          onChange({ ...filter, workTypes: VISIBLE_WORK_TYPES.filter((w) => values.includes(w)) })
        }
      />
      <MultiSelect
        label={t('questions.timings')}
        clearable
        data={RISQ_PREP_TIMINGS.map((p) => ({
          value: p,
          label: t('questions.timingOption', {
            short: t(`common:timings.${p}.short`),
            meaning: t(`common:timings.${p}.meaning`),
          }),
        }))}
        value={filter.timings}
        onChange={(values) =>
          onChange({ ...filter, timings: RISQ_PREP_TIMINGS.filter((p) => values.includes(p)) })
        }
      />
    </SimpleGrid>
  );
}

/** D4 Vessel questions: filter by section, position, status, marks; search by code or text. */
export function VesselQuestionsPage() {
  const { t } = useTranslation('dashboard');
  const { vesselId = '' } = useParams();
  const { vessels } = useWorkplace();
  const vessel = vessels.find((v) => v.id === vesselId);
  const { items, content, loading, error } = useVesselItems(vesselId);
  const { list } = usePositionNames();
  const [filter, setFilter] = useState<QuestionFilter>(EMPTY_FILTER);
  const shown = useMemo(
    () =>
      filterQuestions(
        items ?? [],
        filter,
        (i) => i.positions.codes,
        (i) => i.status,
      ),
    [items, filter],
  );
  if (vessel === undefined) {
    return <Text>{t('vessel.notFound')}</Text>;
  }
  return (
    <>
      <PageTitle
        title={t('questions.title', { vessel: vessel.name })}
        back={{ to: `/vessels/${vessel.id}`, label: vessel.name }}
      />
      <QueryState loading={loading} error={error}>
        {() => (
          <Stack>
            <QuestionFilters
              filter={filter}
              onChange={setFilter}
              sections={content?.sections ?? []}
              withStatus
            />
            <Text fw={600}>{t('questions.count', { count: shown.length })}</Text>
            <Table.ScrollContainer minWidth={900}>
              <Table striped highlightOnHover verticalSpacing="sm">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>{t('questions.code')}</Table.Th>
                    <Table.Th>{t('questions.text')}</Table.Th>
                    <Table.Th>{t('questions.responsible')}</Table.Th>
                    <Table.Th>{t('questions.marks')}</Table.Th>
                    <Table.Th>{t('questions.status')}</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {shown.map((i) => (
                    <Table.Tr key={i.id}>
                      <Table.Td>
                        <Anchor
                          component={Link}
                          to={`/vessels/${vessel.id}/questions/${i.id}`}
                          fw={600}
                        >
                          {i.question.code}
                        </Anchor>
                      </Table.Td>
                      <Table.Td maw={480}>
                        <Text size="sm" lineClamp={3}>
                          {i.question.textEn}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{list(i.positions.codes, t('questions.unassigned'))}</Text>
                        {i.positions.source === 'base' ? null : (
                          <Text size="xs" c="dimmed">
                            {t(`layers.source.${i.positions.source}`)}
                          </Text>
                        )}
                      </Table.Td>
                      <Table.Td>
                        <FlagBadges flags={i.flags} />
                      </Table.Td>
                      <Table.Td>
                        <Group gap={4}>
                          <StatusBadge status={i.status} />
                          {i.attentionFlag ? (
                            <Badge color="red" variant="outline" tt="none">
                              {t('question.attentionShort')}
                            </Badge>
                          ) : null}
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          </Stack>
        )}
      </QueryState>
    </>
  );
}
